import { useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { useMilestones, useCreateMilestone, useUpdateMilestone, useDeleteMilestone } from '@/hooks';
import { MilestoneForm } from './MilestoneForm';
import { MilestoneList } from './MilestoneList';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { Database } from '@/types/database.types';

type Milestone = Database['public']['Tables']['milestones']['Row'];
type MilestoneType = Milestone['type'];

interface MilestoneManagerProps {
  /** Project ID for which to manage milestones */
  projectId: number;
  /** Current user ID */
  userId: string;
}

/**
 * MilestoneManager component handles CRUD operations for project milestones
 * Integrates with React Query for server state management
 */
export const MilestoneManager = ({ projectId, userId }: MilestoneManagerProps) => {
  const { showToast } = useToast();

  // State management
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [milestoneToDelete, setMilestoneToDelete] = useState<Milestone | null>(null);

  // Query and mutations
  const milestonesQuery = useMilestones(projectId);
  const createMilestone = useCreateMilestone();
  const updateMilestone = useUpdateMilestone();
  const deleteMilestone = useDeleteMilestone();

  // Handle create milestone
  const handleCreateMilestone = async (data: {
    name: string;
    target_date: string;
    type: MilestoneType;
    description?: string;
  }) => {
    try {
      await createMilestone.mutateAsync({
        project_id: projectId,
        user_id: userId,
        name: data.name,
        target_date: data.target_date,
        type: data.type,
        description: data.description,
      });
      setIsFormOpen(false);
      showToast({
        title: 'Success',
        message: 'Milestone created successfully',
        type: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create milestone',
        type: 'error',
      });
    }
  };

  // Handle update milestone
  const handleUpdateMilestone = async (data: {
    name: string;
    target_date: string;
    type: MilestoneType;
    description?: string;
  }) => {
    if (!editingMilestone) return;

    try {
      await updateMilestone.mutateAsync({
        id: editingMilestone.id,
        updates: {
          name: data.name,
          target_date: data.target_date,
          type: data.type,
          description: data.description,
        },
      });
      setEditingMilestone(null);
      setIsFormOpen(false);
      showToast({
        title: 'Success',
        message: 'Milestone updated successfully',
        type: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update milestone',
        type: 'error',
      });
    }
  };

  // Handle delete milestone
  const handleConfirmDelete = async () => {
    if (!milestoneToDelete) return;

    try {
      await deleteMilestone.mutateAsync({ id: milestoneToDelete.id, projectId });
      setMilestoneToDelete(null);
      showToast({
        title: 'Success',
        message: 'Milestone deleted successfully',
        type: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete milestone',
        type: 'error',
      });
    }
  };

  // Handle edit milestone
  const handleEditMilestone = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setIsFormOpen(true);
  };

  // Handle close form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingMilestone(null);
    createMilestone.reset();
    updateMilestone.reset();
  };

  // Render loading state
  if (milestonesQuery.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  // Render error state
  if (milestonesQuery.isError) {
    return (
      <Alert variant="error">
        Failed to load milestones. Please try again.
      </Alert>
    );
  }

  const isFormSubmitting = createMilestone.isPending || updateMilestone.isPending;
  const isDeletingMilestone = deleteMilestone.isPending;

  return (
    <div className="space-y-4">
      {/* Header with action button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Project Milestones</h3>
        <Button
          onClick={() => {
            setEditingMilestone(null);
            setIsFormOpen(true);
          }}
          disabled={isFormSubmitting}
        >
          + Add Milestone
        </Button>
      </div>

      {/* Milestones list */}
      <MilestoneList
        milestones={milestonesQuery.data || []}
        onEdit={handleEditMilestone}
        onDelete={(milestone) => setMilestoneToDelete(milestone)}
        isLoading={isFormSubmitting}
      />

      {/* Milestone form modal */}
      <MilestoneForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={editingMilestone ? handleUpdateMilestone : handleCreateMilestone}
        initialData={editingMilestone || undefined}
        isLoading={isFormSubmitting}
        error={createMilestone.error?.message || updateMilestone.error?.message || null}
      />

      {/* Delete confirmation dialog */}
      {milestoneToDelete && (
        <ConfirmDialog
          isOpen={!!milestoneToDelete}
          title="Delete Milestone"
          message={`Are you sure you want to delete "${milestoneToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onClose={() => setMilestoneToDelete(null)}
          isLoading={isDeletingMilestone}
          variant="danger"
        />
      )}
    </div>
  );
};




